import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import GaugeChart from "@/components/growth/GaugeChart";
import { toast } from "sonner";
import { ClipboardCheck, Edit, Send, MessageCircle, BarChart3, CheckCircle2, AlertTriangle } from "lucide-react";

type Tab = "audit" | "editor" | "posts" | "qa" | "performance";

const POST_LIMIT = 1500;

const AUDIT_ITEMS = [
  { label: "Business name set", ok: true },
  { label: "Primary category set", ok: true },
  { label: "Address verified", ok: true },
  { label: "Phone number listed", ok: true },
  { label: "Website link added", ok: true },
  { label: "Hours of operation", ok: true },
  { label: "Cover photo uploaded", ok: true },
  { label: "Logo uploaded", ok: false },
  { label: "10+ photos in gallery", ok: false },
  { label: "Service descriptions", ok: true },
  { label: "Q&A section populated", ok: false },
  { label: "Recent post (within 7 days)", ok: false },
];

export default function GoogleBusiness() {
  const [tab, setTab] = useState<Tab>("audit");
  const [postBody, setPostBody] = useState("");
  const [businessName, setBusinessName] = useState("Compassion at Home Care");
  const [phone, setPhone] = useState("(555) 123-4567");
  const [questions] = useState([
    { q: "Do you accept Medicaid?", a: "Yes — we are credentialed under the Virginia Medicaid waiver program.", date: "1 week ago" },
    { q: "What hours are available?", a: "We provide 24/7 care including overnight and weekends.", date: "3 weeks ago" },
  ]);

  const completeness = Math.round((AUDIT_ITEMS.filter((i) => i.ok).length / AUDIT_ITEMS.length) * 100);

  const remaining = POST_LIMIT - postBody.length;
  const warn = remaining < 50;

  const publishPost = () => {
    if (!postBody.trim()) return toast.error("Write a post first");
    toast.success("Post published to Google Business Profile");
    setPostBody("");
  };

  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: "audit", label: "Audit", icon: ClipboardCheck },
    { key: "editor", label: "Editor", icon: Edit },
    { key: "posts", label: "Posts", icon: Send },
    { key: "qa", label: "Q&A", icon: MessageCircle },
    { key: "performance", label: "Performance", icon: BarChart3 },
  ];

  return (
    <div className="space-y-6">
      <div className="flex gap-1 border-b overflow-x-auto">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 ${tab === t.key ? "border-gold text-navy" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "audit" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Profile completeness</CardTitle></CardHeader>
            <CardContent className="flex justify-center"><GaugeChart value={completeness} label={completeness >= 80 ? "Strong" : "Needs work"} /></CardContent>
          </Card>
          <Card className="md:col-span-2">
            <CardHeader><CardTitle className="text-base">Audit checklist</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-1.5">
                {AUDIT_ITEMS.map((it) => (
                  <div key={it.label} className="flex items-center gap-2 py-1 text-sm">
                    {it.ok ? <CheckCircle2 className="h-4 w-4 text-success" /> : <AlertTriangle className="h-4 w-4 text-warning" />}
                    <span className={it.ok ? "" : "text-muted-foreground"}>{it.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "editor" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Profile editor</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Business name</Label><Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} /></div>
            <div><Label>Phone</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
            <Button className="bg-gold text-gold-foreground hover:bg-gold/90" onClick={() => toast.success("Profile saved")}>Save</Button>
          </CardContent>
        </Card>
      )}

      {tab === "posts" && (
        <Card>
          <CardHeader><CardTitle className="text-base">New post</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={postBody}
              onChange={(e) => setPostBody(e.target.value.slice(0, POST_LIMIT))}
              placeholder="Share an update with your community…"
              rows={6}
            />
            <div className="flex items-center justify-between">
              <span className={`text-xs ${warn ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                {remaining} characters remaining
              </span>
              <Button onClick={publishPost} className="bg-gold text-gold-foreground hover:bg-gold/90"><Send className="h-4 w-4" /> Publish</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === "qa" && (
        <Card>
          <CardHeader><CardTitle className="text-base">Customer questions</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {questions.map((q, i) => (
              <div key={i} className="p-3 border rounded-lg">
                <div className="font-medium text-sm">{q.q}</div>
                <div className="text-sm text-muted-foreground mt-1">{q.a}</div>
                <Badge variant="outline" className="mt-2 text-xs">{q.date}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {tab === "performance" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Profile views (30d)", value: "1,847", delta: "+24%" },
            { label: "Search appearances", value: "12.4k", delta: "+18%" },
            { label: "Direction requests", value: "92", delta: "+11%" },
          ].map((s) => (
            <Card key={s.label}>
              <CardContent className="pt-6">
                <div className="text-sm text-muted-foreground">{s.label}</div>
                <div className="text-3xl font-bold text-navy mt-1">{s.value}</div>
                <div className="text-xs text-success mt-1">{s.delta}</div>
              </CardContent>
            </Card>
          ))}
          <Card className="md:col-span-3">
            <CardHeader><CardTitle className="text-base">Views over time</CardTitle></CardHeader>
            <CardContent>
              <svg viewBox="0 0 600 160" className="w-full h-40">
                <polyline points="0,120 50,100 100,110 150,80 200,90 250,60 300,70 350,40 400,55 450,30 500,45 550,20 600,35" fill="none" stroke="hsl(var(--gold))" strokeWidth="2.5" />
              </svg>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
